import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'

/**
 * Landing page for the "reset your password" email.
 *
 * Supabase puts the recovery token in the URL fragment and the client library
 * exchanges it for a session automatically. So by the time this renders, the
 * visitor is provisionally signed in and updateUser() is all that is needed.
 *
 * The wait matters: if we render the form before that exchange finishes, the
 * update fires with no session and fails with an error that reads like the
 * link was bad. So hold until a session shows up, and only call the link dead
 * once it is clear none is coming.
 */
export default function ResetPassword() {
  const [ready, setReady] = useState<'waiting' | 'ok' | 'expired'>('waiting')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const nav = useNavigate()

  useEffect(() => {
    let settled = false

    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
      if (s) { settled = true; setReady('ok') }
    })
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) { settled = true; setReady('ok') }
    })

    // Recovery links are single-use and short-lived. If nothing has arrived
    // after a beat, the link was already used or has expired.
    const t = setTimeout(() => { if (!settled) setReady('expired') }, 3000)
    return () => { clearTimeout(t); sub.subscription.unsubscribe() }
  }, [])

  const save = async (e: React.FormEvent) => {
    e.preventDefault()
    setErr(null)
    if (password.length < 8) return setErr('Use at least 8 characters.')
    if (password !== confirm) return setErr('Those two do not match.')

    setBusy(true)
    const { error } = await supabase.auth.updateUser({ password })
    setBusy(false)
    if (error) return setErr(error.message)
    nav('/app', { replace: true })
  }

  return (
    <div className="center">
      <div className="card auth-card">
        <Link className="wordmark" to="/">GRIDLOCK</Link>

        {ready === 'waiting' && <p className="muted">Checking your link…</p>}

        {ready === 'expired' && (
          <>
            <h2>That link has expired</h2>
            <p className="muted">
              Reset links work once and time out quickly. Ask for a fresh one.
            </p>
            <Link className="btn full" to="/login">Back to sign in</Link>
          </>
        )}

        {ready === 'ok' && (
          <form onSubmit={save}>
            <h2>Set a new password</h2>
            <label>
              New password
              <input
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                placeholder="At least 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
            <label>
              Confirm
              <input
                type="password"
                required
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </label>
            <button className="btn btn-primary full" disabled={busy}>
              {busy ? 'Saving…' : 'Save and sign in'}
            </button>
            {err && <p className="error">{err}</p>}
          </form>
        )}
      </div>
    </div>
  )
}
